﻿import {ErrorBundle} from "../ErrorBundle";
import {FormattableString} from "../FormattableString";
import {HttpClientError} from "../http/HttpClientError";
import {ProblemDetails} from "../http/ProblemDetails";
import UserError from "../UserError";
import isNullOrEmpty from "./isNullOrEmpty";

const segmentEvaluator: RegExp = /[\w-_]+:[^/]+/g;

function stringToArguments(text: string): Record<string, any> {
	const segments = text.split('|');
	let index = 0;
	const result: Record<string, any> = {};

	for (const segment of segments) {
		result[index.toString(10)] = segment;
	}

	return result;
}

function stringToFormattableString<T extends string | null>(text: T): T extends string ? FormattableString : undefined {
	if (text == null) {
		return undefined as T extends string ? FormattableString : undefined;
	}

	if (segmentEvaluator.test(text)) {
		//text:args
		//Where:
		//- text = "Some text with this {0} or that {1} value."
		//- args = arg0|arg1
		const [literal, args] = text.split(':');
		return {
			text: literal,
			args: stringToArguments(args)
		} as T extends string ? FormattableString : undefined;
	}

	return {
		text: text,
		args: null
	} as T extends string ? FormattableString : undefined;
}

function dataToFooter(sessionId: string | null, requestId: string | null, traceId: string | null): FormattableString | undefined {
	const items: string[] = [];
	if (!isNullOrEmpty(sessionId)) {
		items.push(`ꮪꮖꭰ: ${sessionId}`);
	}

	if (!isNullOrEmpty(traceId)) {
		items.push(`ꭲꮖꭰ: ${traceId}`);
	}

	if (!isNullOrEmpty(requestId)) {
		items.push(`ꭱꮖꭰ: ${requestId}`);
	}

	return items.length == 0 ? undefined : {text: items.join(' | ')};
}

function problemDetailsDataToErrors(problem: ProblemDetails): FormattableString[] {
	const errors = problem.errors;
	const result: FormattableString[] = [];
	if (errors == null) return result;

	if (Array.isArray(errors)) {
		//array of key/value pairs
		for (const error of errors as Record<string, string>[]) {
			result.push(...Object.values(error).filter(e => !isNullOrEmpty(e)).map(e => stringToFormattableString(e)!))
		}

		return result
	}

	//object with an array
	for (const error of Object.values(errors)) {
		const errorList: string[] = Array.isArray(error) ? error : [error];
		result.push(...errorList.filter(e => !isNullOrEmpty(e)).map(e => stringToFormattableString(e)!))
	}

	return result;
}

function httpClientErrorToDialog(error: HttpClientError): ErrorBundle {
	const problem = error.problemDetails;
	const sessionId: string | null = error.getRequestHeader("x-session-id");
	const requestId: string | null = problem["requestId"] as string ?? error.response?.headers.get("x-request-id");
	const traceId: string | null = problem["traceId"] as string ?? error.response?.headers.get("x-trace-id");

	let title: string;
	let detail: string | null;
	if (problem == null) {
		title = error.name;
		detail = error.message;
	} else {
		title = problem.title ?? error.name;
		detail = problem.detail || null;
	}

	return {
		title: stringToFormattableString(title),
		detail: stringToFormattableString(detail),
		errors: problemDetailsDataToErrors(problem),
		footer: dataToFooter(sessionId, requestId, traceId),
		stack: error.stack
	};
}

export default function errorToErrorBundle(error: Error): ErrorBundle {
	if (error instanceof HttpClientError) {
		return httpClientErrorToDialog(error);
	}

	if (error instanceof UserError) {
		return {
			title: error.title,
			detail: error.detail,
			errors: error.errors ?? [],
			footer: error.footer
		}
	}

	return {
		title: stringToFormattableString(error.name),
		detail: stringToFormattableString(error.message),
		errors: [],
		footer: undefined,
		stack: error.stack
	}
}
